import { zodResolver } from '@hookform/resolvers/zod';
import { Alert as AlertIcon } from 'baseui/icon';
import { Input } from 'baseui/input';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'baseui/modal';
import { useSnackbar } from 'baseui/snackbar';
import { Textarea } from 'baseui/textarea';
import clsx from 'clsx';
import { addDays, isAfter, isEqual, startOfDay } from 'date-fns';
import {
  toSeconds as ISODurationToSeconds,
  parse as parseISODuration,
} from 'iso8601-duration';
import parseHumanDuration from 'parse-duration';
import prettyMilliseconds from 'pretty-ms';
import { useEffect, useMemo, useState } from 'react';
import { Heading } from 'react-aria-components';
import {
  Check,
  CheckCircle as CheckCircleIcon,
  CreditCard,
  Edit,
  Info as InfoIcon,
  Mail,
  Navigation,
  Phone,
  Save,
  XCircle as XCircleIcon,
} from 'react-feather';
import {
  ControllerRenderProps,
  UseFormSetValue,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useParams, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { LineItems, ServLocAddressForm, schema } from './index';

import BillToForm from './BillToForm';
import PaymentModal from './PaymentModal';
import { InvoiceFormFields } from './schema';
import ServiceLocationNotes from './ServiceLocationNotes';
import WorkRequestAttachments from './WorkRequestAttachments';
import WorkRequestChecklists from './WorkRequestChecklists';
import WorkRequestOverview from './WorkRequestOverview';

import { Button } from 'components/Button';
import { FormController } from 'components/FormController';
import { DataStoreImage } from 'components/Image';
import { QueryResolver } from 'components/QueryResolver';
import {
  Alert,
  AlertDescription,
  AlertFooter,
  AlertTitle,
} from 'components/ui/alert';
import { Badge } from 'components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import StatusBadge from 'features/work-requests/StatusBadge';
import { useCurrentUser } from 'services/api/auth/endpoints';
import { isMutationFailed, isMutationSuccess } from 'services/api/base-api';
import {
  useIsQuickbooksIntegratedQuery,
  useClientPropertiesQuery,
} from 'services/api/clients/endpoints';
import { formatAddress } from 'services/api/helpers';
import {
  InvoiceService,
  useInvoiceQuery,
  useInvoiceUpdateBalanceMutation,
  useSaveInvoiceMutation,
  useSendInvoiceMutation,
} from 'services/api/invoices/endpoints';
import {
  InvoiceDetails,
  SentInvoiceDestination,
} from 'services/api/invoices/types';
import {
  useGetPaymentHistoryQuery,
  useSyncQuickbooksPaymentMutation,
} from 'services/api/payment/endpoints';
import { HelcimTransactionResponse } from 'services/api/payment/types';
import {
  useSaveServiceLocationMutation,
  useServiceLocationQuery,
} from 'services/api/service-locations/endpoints';
import {
  Address,
  InvoiceAmounts,
  invoiceSpecificsSchema,
} from 'services/api/types.shared';
import {
  useImagesQuery,
  usePreviewPDFQuery,
} from 'services/api/utilities/endpoint';
import {
  useDeleteLineItemsMutation,
  useSaveLineItemsMutation,
  useUpdateWorkRequestMutation,
  useWorkRequestQuery,
} from 'services/api/work-requests/endpoints';
import { formatDate, priceFormatter } from 'utils/helpers';
import { feature_flags } from 'utils/flags';
import { useLocale } from 'utils/hooks/useLocale';

type PaymentHistoryItem = {
  id: number;
  dateModified: string;
  quickbooksId: string;
  currency: 'USD' | 'CAD';
  invoiceId: number;
  amount: number;
  invoiceNumber: string;
};

// type FormFields = z.infer<typeof schema>;
type FormFields = InvoiceFormFields;

type InvoicePanelProps = {
  /**
   * breakdown or amounts properties can be partially supplied
   * to cover scenarios wherein the invoice is being created has an estimate attached.
   * so there are already existing values like `deposit` or `tax`
   */
  invoice: Omit<InvoiceDetails, 'breakdown'> & {
    breakdown: Partial<InvoiceAmounts> | null;
  };
  action?: 'CREATE' | 'UPDATE';
  // onSubmit: (data: FormFields) => Promise<void>;
};
function makeSchemaWithCatchValues<Schema extends z.ZodObject<any>>(
  schema: Schema,
  catchValues: z.infer<typeof schema>,
) {
  const entries = Object.entries(schema.shape) as [
    keyof Schema['shape'],
    z.ZodTypeAny,
  ][];
  const newProps = entries.reduce(
    (acc, [key, value]) => {
      acc[key] = value.catch(catchValues[key]);
      return acc;
    },
    {} as {
      [key in keyof Schema['shape']]: Schema['shape'][key];
    },
  );
  return z.object(newProps);
}

function makeSchemaRequired<Schema extends z.ZodObject<any>>(schema: Schema) {
  const entries = Object.entries(schema.shape) as [
    keyof Schema['shape'],
    z.ZodTypeAny,
  ][];
  const newProps = entries.reduce(
    (acc, [key, value]) => {
      acc[key] = value instanceof z.ZodOptional ? value.unwrap() : value;
      return acc;
    },
    {} as {
      [key in keyof Schema['shape']]: Schema['shape'][key] extends z.ZodOptional<
        infer T
      >
        ? T
        : Schema['shape'][key];
    },
  );
  return z.object(newProps);
}

const invoiceCatchValues: z.infer<typeof invoiceSpecificsSchema> = {
  id: 0,
  workRequestId: 0,
  notes: '',
  dateSent: '',
  dateDue: '',
  datePaid: '',
  subTotal: 0,
  taxable: 0,
  taxPercentage: 0,
  tax: 0,
  deposit: 0,
  total: 0,
  createdById: 0,
  dateCreated: '',
  dateModified: '',
  isDeleted: false,
  dateDeleted: '',
  quickbooksBalance: 0,
  quickbooksId: '',
  currency: 'USD',
};
const invoiceSchemaWithCatchValues = makeSchemaWithCatchValues(
  invoiceSpecificsSchema,
  invoiceCatchValues,
).catch(invoiceCatchValues);

const formComputationsWithCatchValues = makeSchemaWithCatchValues(
  makeSchemaRequired(schema.shape.computations),
  {
    subTotal: 0,
    taxableAmount: 0,
    taxPercentage: 0,
    taxedAmount: 0,
    itemCost: 0,
    total: 0,
    estimatedHours: 0,
    requireDeposit: false,
    depositPercentage: 0,
    depositAmount: 0,
    createdInvoiceId: 0,
  },
);

const parseNumber = (value: unknown) => z.coerce.number().catch(0).parse(value);

const ISODurationToMilliseconds = (stringLikeDuration: string) => {
  return ISODurationToSeconds(parseISODuration(stringLikeDuration)) * 1000;
};

export default function Invoice({
  invoice,
  action = 'CREATE',
}: InvoicePanelProps) {
  const { t } = useLocale();
  const breakdown = invoiceSchemaWithCatchValues.parse(invoice.breakdown);
  const userId = useCurrentUser().data?.id ?? 0;
  const [showPreview, setShowPreview] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const [saveLineItems] = useSaveLineItemsMutation();
  const [deleteLineItems] = useDeleteLineItemsMutation();
  const [saveInvoice] = useSaveInvoiceMutation();
  const [saveServLoc] = useSaveServiceLocationMutation();
  const [sendInvoice] = useSendInvoiceMutation();
  const [updateWorkRequest] = useUpdateWorkRequestMutation();
  const [updateInvoiceBalance] = useInvoiceUpdateBalanceMutation();

  const propertiesQuery = useClientPropertiesQuery();

  const isHelcimConnected = propertiesQuery.data?.some(
    (p) => p.propertyName === 'helcimApiToken' && p.propertyValue,
  );

  const currencyPreference =
    (propertiesQuery.data?.find((p) => p.propertyName === 'currencyPreference')
      ?.propertyValue as 'USD' | 'CAD') || 'USD';

  const isPickupLocationEnabled = (() => {
    const value = propertiesQuery.data?.find(
      (p) => p.propertyName === 'pickupLocation',
    )?.propertyValue;
    if (value === undefined || value === null || value === '') {
      return false;
    }
    return value === 'true';
  })();

  const isPortDeliveryEnabled =
    propertiesQuery.data?.find((p) => p.propertyName === 'portDelivery')
      ?.propertyValue === 'true';

  // Fetch the work request to get pickupServLocId
  const workRequestQuery = useWorkRequestQuery(invoice.workRequestDetails.id);
  const pickupServLocId = workRequestQuery.data?.pickupServLocId;

  // Query pickup service location if pickupServLocId exists
  const pickupLocationQuery = useServiceLocationQuery(pickupServLocId ?? 0, {
    skip: !pickupServLocId || !isPickupLocationEnabled,
  });

  const pickupLocationData = pickupLocationQuery.data;

  const { data: paymentHistory } = useGetPaymentHistoryQuery(
    invoice.breakdown?.id ?? 0,
    {
      skip: !invoice.breakdown?.id,
    },
  );

  const [syncQuickbooks, { isLoading: isSyncing }] =
    useSyncQuickbooksPaymentMutation();

  const quickBooksConnectedQuery = useIsQuickbooksIntegratedQuery(undefined, {
    refetchOnFocus: true,
  });
  const isQuickbooksConnected =
    quickBooksConnectedQuery.data?.isLoggedIn ?? false;
  const centralizedNotesEnabled = feature_flags.centralized_notes.isEnabled();

  const snackbar = useSnackbar();
  // Alternative Schema if pickupLocation is not enabled
  const optionalPickupSchema = useMemo(
    () =>
      schema.extend({ pickupLocation: schema.shape.pickupLocation.optional() }),
    [],
  );

  const formSchema =
    isPickupLocationEnabled && pickupLocationData
      ? schema
      : optionalPickupSchema;

  const { control, handleSubmit, register, watch, setValue, formState } =
    useForm<FormFields>({
      defaultValues: {
        description: invoice.workRequestDetails.workTypeDescription,
        notes: invoice.breakdown?.notes ?? '',
        poNumber: invoice.workRequestDetails.poNumber ?? null,
        soNumber: invoice.workRequestDetails.soNumber ?? null,
        shipName: invoice.workRequestDetails.shipName ?? null,
        items: invoice.lineItems.map((item) => {
          const durationInMs = item.duration
            ? ISODurationToMilliseconds(item.duration)
            : 0;
          return {
            ...item,
            duration:
              durationInMs === 0 ? '' : prettyMilliseconds(durationInMs),
            workTypeId: item.workTypeId,
          };
        }),
        serviceLocation: {
          ...invoice.servLoc.address,
          serviceLocationId: invoice.servLoc.id,
        },
        pickupLocation: undefined,
        billTo: {
          id: invoice.servLoc.customerId,
          firstName: invoice.workRequestDetails.custName.split(' ')[0] || '',
          lastName:
            invoice.workRequestDetails.custName.split(' ').slice(1).join(' ') ||
            '',
          email: invoice.workRequestDetails.email,
          phone: invoice.workRequestDetails.phone,
          businessName: null,
          address: {
            ...invoice.workRequestDetails.address,
            id: invoice.workRequestDetails.address.id,
            street: invoice.workRequestDetails.address.street,
            street2: invoice.workRequestDetails.address.street2 ?? '',
            city: invoice.workRequestDetails.address.city,
            state: invoice.workRequestDetails.address.state,
            postalCode: invoice.workRequestDetails.address.postalCode,
            country: invoice.workRequestDetails.address.country,
            lat: invoice.workRequestDetails.address.lat,
            lng: invoice.workRequestDetails.address.lng,
            timeZone: invoice.workRequestDetails.address.timeZone ?? '',
          },
        },
        computations: {
          ...breakdown,
          taxableAmount: breakdown.taxable,
          taxedAmount: breakdown.tax,
          taxPercentage: breakdown.taxPercentage,
          requireDeposit: !!breakdown.deposit,
          depositPercentage: breakdown.deposit,
          depositAmount: 0,
          // These values will not be used for MVP
          estimatedHours: 0,
          itemCost: 0,
        },
      },
      resolver: zodResolver(formSchema),
    });

  const computationsField = watch('computations');
  const computations = formComputationsWithCatchValues.parse(computationsField);

  const lineItems = watch('items', []);
  const lineItemsField = useFieldArray({
    control,
    name: 'items',
    keyName: 'item-id',
  });

  useEffect(() => {
    setValue(
      'computations.taxableAmount',
      lineItems
        .filter((item) => item.isTaxable)
        .reduce((all, item) => all + item.totalPrice, 0 as number),
    );

    setValue(
      'computations.subTotal',
      lineItems.reduce((all, item) => all + item.totalPrice, 0 as number),
    );
  }, [lineItems, setValue]);

  useEffect(() => {
    const taxPercentage = parseNumber(computations.taxPercentage) / 100;
    const taxedAmount = computations.taxableAmount * taxPercentage;
    const total = computations.subTotal + taxedAmount;
    const depositAmount = (computations.depositPercentage / 100) * total;
    setValue('computations.taxedAmount', taxedAmount);
    setValue('computations.total', total - depositAmount);
  }, [
    computations.depositPercentage,
    computations.taxPercentage,
    computations.taxableAmount,
    computations.subTotal,
    computations.depositAmount,
    setValue,
  ]);

  useEffect(() => {
    if (pickupLocationData && isPickupLocationEnabled) {
      setValue('pickupLocation', {
        ...pickupLocationData.address,
        serviceLocationId: pickupLocationData.id,
      });
    }
  }, [pickupLocationData, isPickupLocationEnabled, setValue]);

  const handleModifyInvoice = async (
    data: FormFields,
    type?: 'SAVE' | 'SAVE&SEND',
  ) => {
    const saveType = type ?? 'SAVE';
    const hasCreatedInvoice = invoice.breakdown?.id !== undefined;
    const workType = data.items[0];
    const removedItems = invoice.lineItems.filter(
      (lineItem) =>
        !data.items.find((modifiedItem) => modifiedItem.id === lineItem.id),
    );

    if (isQuickbooksConnected && invoice.breakdown?.quickbooksId) {
      if (invoice.breakdown?.id) {
        updateInvoiceBalance({ invoiceId: invoice.breakdown.id }).then(
          async () => {
            await InvoiceService.endpoints.fetchInvoiceDetails.useQuery({
              workRequestId: invoice.workRequestDetails.id,
            });
            // message: Invoice balance updated
            snackbar.enqueue({
              message: t('invoice_update_balance_success'),
              startEnhancer: ({ size }) => <Check size={size} />,
            });
          },
        );
      }
    }

    // Edit service location
    const { serviceLocationId, ...address } = data.serviceLocation;
    // const servLocsResult = await ;
    // const workRequestResult = await ;

    const servLocsResult = await saveServLoc({
      ...invoice.servLoc,
      customerId: data.billTo.id,
      address: { ...address },
    });

    let newServLocId = serviceLocationId;
    if (!isMutationFailed(servLocsResult) && servLocsResult.data) {
      newServLocId = servLocsResult.data.id;
    }

    // Edit Pickup Location if it is enabled in Client Admin Settings
    let newPickupServLocId = pickupServLocId;
    if (isPickupLocationEnabled && data.pickupLocation && pickupLocationData) {
      const { serviceLocationId: pickupServiceLocationId, ...pickupAddress } =
        data.pickupLocation;
      const pickupServLocsResult = await saveServLoc({
        ...pickupLocationData,
        customerId: data.billTo.id,
        address: { ...pickupAddress },
      });

      if (
        !isMutationFailed(pickupServLocsResult) &&
        pickupServLocsResult.data
      ) {
        newPickupServLocId = pickupServLocsResult.data.id;
      }
    }

    // Edit line items and work type description
    const bulkResults = await Promise.all([
      saveServLoc({
        ...invoice.servLoc,
        customerId: data.billTo.id,
        address: { ...address },
      }),
      // If Job is in Progress we will not call updateWorkRequest yet, (exception for PO Number)
      ...(invoice.workRequestDetails.progress === 'Started'
        ? [
            updateWorkRequest({
              id: invoice.workRequestDetails.id,
              poNumber: data.poNumber === '' ? null : data.poNumber || null,
              ...(isPortDeliveryEnabled
                ? {
                    soNumber: data.soNumber === '' ? null : data.soNumber || null,
                    shipName: data.shipName === '' ? null : data.shipName || null,
                  }
                : {}),
              servLocId: newServLocId,
              ...(isPickupLocationEnabled
                ? { pickupServLocId: newPickupServLocId }
                : {}),
              mutationOptions: { invalidate: false },
            }),
          ]
        : [
            updateWorkRequest({
              id: invoice.workRequestDetails.id,
              description: data.description ?? '',
              poNumber: data.poNumber === '' ? null : data.poNumber || null,
              ...(isPortDeliveryEnabled
                ? {
                    soNumber: data.soNumber === '' ? null : data.soNumber || null,
                    shipName: data.shipName === '' ? null : data.shipName || null,
                  }
                : {}),
              servLocId: newServLocId,
              ...(isPickupLocationEnabled
                ? { pickupServLocId: newPickupServLocId }
                : {}),
              ...(hasCreatedInvoice
                ? {}
                : {
                    status: 'InvoiceCreated',
                    prevStatus: invoice.workRequestDetails.progress,
                  }),
              mutationOptions: { invalidate: false },
            }),
          ]),
      saveLineItems([
        {
          ...workType,
          durationInSeconds: parseHumanDuration(workType.duration, 's') || 0,
        },
        ...data.items
          .filter((item) => !!item.name)
          .filter((item) => item.id !== workType.id)
          .map((item) => {
            const { id, ...requiredProperties } = item;
            const durationInSeconds =
              parseHumanDuration(requiredProperties.duration, 's') || 0;
            if (id === 0) {
              return {
                ...requiredProperties,
                durationInSeconds,
                workRequestId: invoice.workRequestDetails.id,
              };
            }
            return {
              id,
              ...requiredProperties,
              durationInSeconds,
            };
          }),
      ]),
      deleteLineItems(removedItems.map((item) => item.id)),
    ]);

    // Edit computations and notes
    const createdById = invoice.breakdown?.createdById ?? userId;
    const amounts = data.computations;
    const invoiceResult = await saveInvoice({
      id: invoice.breakdown?.id,
      ...(invoice.breakdown ? invoice.breakdown : {}),
      createdById,
      workRequestId: invoice.workRequestDetails.id,
      notes: data.notes ?? '',
      subTotal: amounts.subTotal,
      tax: amounts.taxedAmount,
      taxable: amounts.taxableAmount,
      taxPercentage: amounts.taxPercentage,
      total: amounts.total,
      deposit: amounts.depositPercentage,
      quickbooksBalance: invoice.breakdown?.quickbooksBalance ?? 0,
      quickbooksId: invoice.breakdown?.quickbooksId ?? '',
      /**
       * FIXME:
       * Temporary patch since when creating a `invoice` the `dateDue`
       * is required.
       */
      dateDue: invoice.breakdown?.dateDue ?? new Date().toISOString(),
      currency: invoice.breakdown?.currency ?? 'USD',
      mutationOptions: {
        invalidate: saveType === 'SAVE',
      },
    });

    const failedUpdates = [...bulkResults, invoiceResult].find((r) =>
      isMutationFailed<unknown>(r),
    );

    if (failedUpdates) {
      return snackbar.enqueue({
        // message: `Failed ${action === 'CREATE' ? 'create' : 'update'} invoice`,
        message:
          action === 'CREATE'
            ? t('invoice_create_error')
            : t('invoice_update_error'),
        startEnhancer: ({ size }) => <AlertIcon size={size} />,
      });
    }

    if (saveType === 'SAVE') {
      return snackbar.enqueue({
        // message: `Invoice ${action === 'CREATE' ? 'created' : 'updated'}`,
        message:
          action === 'CREATE'
            ? t('invoice_create_success')
            : t('invoice_update_success'),
        startEnhancer: ({ size }) => <Check size={size} />,
      });
    }

    snackbar.dequeue();
    if (!invoice.breakdown) {
      return snackbar.enqueue({
        // message: 'Invoice cannot be sent. Please create an invoice first.',
        message: t('invoice_send_not_permitted'),
      });
    }

    const dateSent = invoice.breakdown.dateSent
      ? new Date(invoice.breakdown.dateSent)
      : new Date();
    const dateDue = addDays(dateSent, 14);
    const sendInvoiceTo: SentInvoiceDestination = isQuickbooksConnected
      ? 'TO-QUICKBOOKS'
      : 'TO-CUSTOMER';

    const result = await sendInvoice({
      workRequestId: invoice.workRequestDetails.id,
      dateSent: dateSent.toISOString(),
      dateDue: dateDue.toISOString(),
      invoiceTo: sendInvoiceTo,
    });

    snackbar.dequeue();
    if (!isMutationSuccess(result)) {
      return snackbar.enqueue(
        {
          // message:
          //   sendInvoiceTo === 'TO-QUICKBOOKS'
          //     ? 'Failed to export invoice to Quickbooks'
          //     : `Failed to send invoice`,
          message:
            sendInvoiceTo === 'TO-QUICKBOOKS'
              ? t('invoice_quickbooks_send_error')
              : t('invoice_email_send_error'),
          startEnhancer: ({ size }) => <AlertIcon size={size} />,
        },
        3000,
      );
    }

    snackbar.enqueue(
      {
        // message:
        //   sendInvoiceTo === 'TO-QUICKBOOKS'
        //     ? `Invoice ${
        //         action === 'CREATE' ? 'created' : 'updated'
        //       } and has been sent to your Quickbooks account.`
        //     : `Invoice ${action === 'CREATE' ? 'created' : 'updated'} ${
        //         saveType === 'SAVE&SEND' ? 'and Sent' : ''
        //       }`,
        message:
          sendInvoiceTo === 'TO-QUICKBOOKS'
            ? t('invoice_quickbooks_send_save_success')
            : t('invoice_email_sent_save_success'),
        startEnhancer: ({ size }) => <Check size={size} />,
      },
      4000,
    );
  };

  const onSubmit = handleSubmit((data) => handleModifyInvoice(data));
  const onSend = handleSubmit((data) => handleModifyInvoice(data, 'SAVE&SEND'));
  const onMarkAsPaid = handleSubmit(async (data) => {
    snackbar.dequeue();
    if (!invoice.breakdown) {
      return;
    }
    const workRequestRevertPayload: Parameters<typeof updateWorkRequest>[0] = {
      id: invoice.workRequestDetails.id,
      status: invoice.workRequestDetails.progress,
      prevStatus: 'InvoiceCreated',
    };

    const updateWorkRequestError = isMutationFailed(
      await updateWorkRequest({
        id: invoice.workRequestDetails.id,
        status: 'Collected',
        prevStatus: invoice.workRequestDetails.progress,
        mutationOptions: { invalidate: false },
      }),
    );

    if (updateWorkRequestError) {
      return snackbar.enqueue({
        message: t('invoice_update_payment_status_error'),
      });
    }

    const saveInvoiceError = isMutationFailed(
      await saveInvoice({
        ...breakdown,
        datePaid: new Date().toISOString(),
      }),
    );

    if (saveInvoiceError) {
      // We revert if save invoice fails. this also invalidates data and shows user the current state
      await updateWorkRequest(workRequestRevertPayload);
      return snackbar.enqueue({
        message: t('invoice_update_payment_status_error'),
      });
    }
    snackbar.enqueue({ message: t('invoice_update_payment_status_success') });
  });
  /**
   * TODO: Should revert if one request fails
   */
  const onUndoPayment = handleSubmit(async (data) => {
    snackbar.dequeue();
    if (!invoice.breakdown) {
      return;
    }
    const workRequestRevertPayload: Parameters<typeof updateWorkRequest>[0] = {
      id: invoice.workRequestDetails.id,
      status: invoice.workRequestDetails.progress,
      prevStatus: 'InvoiceSent',
    };

    const updateWorkRequestError = isMutationFailed(
      await updateWorkRequest({
        id: invoice.workRequestDetails.id,
        status: 'InvoiceSent',
        prevStatus: invoice.workRequestDetails.progress,
        mutationOptions: { invalidate: false },
      }),
    );

    if (updateWorkRequestError) {
      return snackbar.enqueue({
        message: t('invoice_update_payment_status_error'),
      });
    }

    const saveInvoiceError = isMutationFailed(
      await saveInvoice({
        ...breakdown,
        datePaid: null,
      }),
    );

    if (saveInvoiceError) {
      // We revert if save invoice fails. this also invalidates data and shows user the current state
      await updateWorkRequest(workRequestRevertPayload);
      return snackbar.enqueue({
        message: t('invoice_update_payment_status_error'),
      });
    }
    snackbar.enqueue({
      message: t('invoice_update_payment_status_reverted_success'),
    });
  });

  const isSameAsBilling = (addressToCompare: Address) =>
    formatAddress(invoice.workRequestDetails.address) ===
    formatAddress(addressToCompare);

  const isLoading = formState.isSubmitting;
  const lineItemErrors =
    formState.errors.items && Array.isArray(formState.errors.items)
      ? formState.errors.items
      : [];
  const isDue = invoice.workRequestDetails.progress === 'InvoiceSent';
  const isOverdue =
    invoice.breakdown?.dateDue && isDue
      ? /**
       * reset both dates' time to 00:00:00 for consistency
      since `isAfter` returns `false` when dates are equal we fallback to `isEqual`
       */
        isAfter(
          startOfDay(new Date()),
          startOfDay(new Date(invoice.breakdown.dateDue)),
        ) ||
        isEqual(
          startOfDay(new Date()),
          startOfDay(new Date(invoice.breakdown.dateDue)),
        )
      : false;
  const isPaid =
    !!invoice.breakdown?.datePaid &&
    invoice.workRequestDetails.progress === 'Collected';

  const handlePaymentComplete = async (response: HelcimTransactionResponse) => {
    snackbar.enqueue({
      message: t('payment_success_message', {
        amount: priceFormatter().format(Number(response.amount)),
      }),
      startEnhancer: ({ size }) => <Check size={size} />,
    });
  };

  const formatCurrencyAmount = (amount: number, currency: 'USD' | 'CAD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const calculateTotalPayments = (
    payments: PaymentHistoryItem[] | undefined,
  ): number => {
    if (!payments || !Array.isArray(payments)) {
      return 0;
    }
    return payments.reduce((sum, payment) => sum + payment.amount, 0);
  };

  const remainingBalance =
    computations.total - calculateTotalPayments(paymentHistory);

  return (
    <Card>
      <CardHeader>
        {(isDue || isPaid) && (
          <div className="mb-4">
            <Alert>
              <InfoIcon className="h-4 w-4" />
              <div>
                <AlertTitle>{t('Invoice Sent')}</AlertTitle>
                <AlertDescription>
                  {t('invoice_sent_alert_description')}
                </AlertDescription>
                <div className="mt-3 flex space-x-2">
                  {!isPaid ? (
                    <Badge color="yellow">{t('Awaiting Payment')}</Badge>
                  ) : (
                    <Badge color="green">{t('Invoice Paid')}</Badge>
                  )}
                  {isOverdue && <Badge color="red">{t('Overdue')}</Badge>}
                </div>
                <div className="mt-5 border-t border-muted pt-1">
                  <dl className="flex space-x-6">
                    <div className="px-2 py-3 sm:col-span-1 sm:px-0">
                      <dt className="text-sm font-semibold leading-6">
                        {t('Date Sent')}
                      </dt>
                      <dd className="mt-0.5 text-sm leading-6">
                        {invoice.breakdown?.dateSent
                          ? formatDate(new Date(invoice.breakdown.dateSent))
                          : '---'}
                      </dd>
                    </div>
                    <div className="px-2 py-3 sm:col-span-1 sm:px-0">
                      <dt className="text-sm font-semibold leading-6 ">
                        {t('Date Due')}
                      </dt>
                      <dd className="mt-0.5 text-sm leading-6">
                        {invoice.breakdown?.dateSent &&
                        invoice.breakdown?.dateDue
                          ? formatDate(new Date(invoice.breakdown.dateDue))
                          : '---'}
                      </dd>
                    </div>
                    <div className="px-2 py-3 sm:col-span-1 sm:px-0">
                      <dt className="text-sm font-semibold leading-6 ">
                        {t('Date Paid')}
                      </dt>
                      <dd className="mt-0.5 text-sm leading-6">
                        {invoice.breakdown?.datePaid
                          ? formatDate(new Date(invoice.breakdown.datePaid))
                          : '---'}
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
              <AlertFooter className="flex justify-end">
                {isPaid ? (
                  <Button
                    size="compact"
                    type="button"
                    intent="danger"
                    startEnhancer={<XCircleIcon className="h-4 w-4" />}
                    onClick={onUndoPayment}
                    isLoading={isLoading}
                  >
                    {t('Mark as Unpaid')}
                  </Button>
                ) : (
                  <Button
                    intent="success"
                    size="compact"
                    type="button"
                    startEnhancer={<CheckCircleIcon className="h-4 w-4" />}
                    onClick={onMarkAsPaid}
                    isLoading={isLoading}
                  >
                    {t('Mark as Paid')}
                  </Button>
                )}
              </AlertFooter>
            </Alert>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-y-4">
          <CardTitle>{t('Invoice Details')}</CardTitle>
          <nav className="flex items-center space-x-2">
            {action === 'UPDATE' && !isPaid && isHelcimConnected && (
              <div
                onMouseEnter={() => {
                  if (!['USD', 'CAD'].includes(currencyPreference)) {
                    snackbar.enqueue({
                      // Helcim payment only supports USD and CAD currencies.
                      message: t('helcim_pay_button_disabled_description'),
                      startEnhancer: ({ size }) => <AlertIcon size={size} />,
                    });
                  }
                }}
              >
                <Button
                  size="compact"
                  startEnhancer={<CreditCard size={16} />}
                  kind="secondary"
                  type="button"
                  onClick={() => setShowPaymentModal(true)}
                  disabled={
                    isLoading ||
                    quickBooksConnectedQuery.isLoading ||
                    !['USD', 'CAD'].includes(currencyPreference) ||
                    remainingBalance <= 0
                  }
                >
                  {remainingBalance <= 0 ? t('Fully Paid') : t('Pay')}
                </Button>
              </div>
            )}
            <Button
              size="compact"
              startEnhancer={<Save size={16} />}
              kind="secondary"
              type="submit"
              form="invoice-form"
              isLoading={isLoading}
              disabled={quickBooksConnectedQuery.isLoading || isPaid}
            >
              {action === 'CREATE' ? t('Create Invoice') : t('Update Invoice')}
            </Button>
            {action === 'UPDATE' && (
              <Button
                size="compact"
                startEnhancer={<Mail size={16} />}
                kind="secondary"
                type="button"
                isLoading={isLoading}
                onClick={onSend}
                disabled={
                  quickBooksConnectedQuery.isLoading ||
                  isPaid ||
                  computations.total === 0 ||
                  invoice.breakdown?.quickbooksBalance === 0
                }
              >
                {t('Send')}
              </Button>
            )}
            <Button
              size="compact"
              type="button"
              disabled={isLoading}
              onClick={() => setShowPreview((prev) => !prev)}
            >
              {showPreview ? t('Continue Editing') : t('Preview Invoice')}
            </Button>
          </nav>
        </div>
      </CardHeader>
      <CardContent>
        <form id="invoice-form" onSubmit={onSubmit} className="space-y-7">
          {isPickupLocationEnabled && pickupLocationData ? (
            <dl className="grid gap-x-6 gap-y-4 font-prompt lg:grid-cols-2 xl:grid-cols-2">
              <div className="col-span-full flex items-center space-x-2">
                <div>
                  <dt className="sr-only">Work Request number</dt>
                  <dd className="text-4xl font-semibold text-brand-primary">
                    #{invoice.workRequestDetails.num} -{' '}
                  </dd>
                </div>
                <div>
                  <dt className="sr-only">status</dt>
                  <dd>
                    <StatusBadge
                      size="large"
                      status={invoice.workRequestDetails.progress}
                    />
                  </dd>
                </div>
              </div>
              <div className="space-y-2">
                <FormController control={control} name="billTo">
                  {({ field }) => (
                    <>
                      <div>
                        <dt className="flex items-center space-x-3 text-lg font-semibold">
                          <span>{t('Bill To')}: </span>
                          <BillToField {...field} setValue={setValue} />
                        </dt>
                        <dl className="space-y-3">
                          <div className="flex items-center">
                            <dt className="sr-only">{t('Customer Name')}</dt>
                            <dd className="pt-2 text-base font-semibold text-gray-500">
                              {`${field.value.firstName} ${field.value.lastName}`}
                            </dd>
                          </div>
                          <div className="flex items-start space-x-4">
                            <dt
                              aria-label="Address"
                              className="pt-1.5 text-base text-gray-500"
                            >
                              <Navigation size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {formatAddress(field.value.address)}
                            </dd>
                          </div>
                          <div className="flex space-x-4">
                            <dt
                              aria-label="Phone number"
                              className="pt-1 text-base text-gray-500"
                            >
                              <Phone size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {field.value.phone}
                            </dd>
                          </div>
                          <div className="flex space-x-4">
                            <dt
                              aria-label="email address"
                              className="pt-1.5 text-base text-gray-500"
                            >
                              <Mail size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {field.value.email}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </>
                  )}
                </FormController>
              </div>
              <div className="flex flex-col space-y-4">
                <div className="col-span-full space-y-2 xl:col-span-1">
                  <FormController
                    control={control}
                    name="description"
                  >
                    {({ field }) => (
                      <>
                        <WorktypeDescription>
                          {({ editable, setEditable }) => (
                            <>
                              <div className="flex items-center space-x-3 text-lg font-semibold">
                                <dt>{t('Work Details')}</dt>

                                <Button
                                  title="Edit"
                                  size="mini"
                                  shape="square"
                                  kind="secondary"
                                  type="button"
                                  onClick={() => setEditable(!editable)}
                                >
                                  <Edit size={12} />
                                </Button>
                              </div>
                              <div>
                                {editable ? (
                                  <div className="space-y-4 py-2">
                                    <Textarea
                                      name={field.name}
                                      value={field.value}
                                      onChange={(e) => {
                                        field.onChange(e.target.value);
                                      }}
                                      placeholder={t('Description (Optional)')}
                                      onBlur={field.onBlur}
                                    />
                                    <nav className="flex justify-end space-x-2">
                                      <Button
                                        size="mini"
                                        kind="secondary"
                                        onClick={() => setEditable(false)}
                                      >
                                        {t('Confirm')}
                                      </Button>
                                    </nav>
                                  </div>
                                ) : (
                                  <dd className="text-base text-gray-500 line-clamp-5">
                                    {field.value ?? 'No description'}
                                  </dd>
                                )}
                              </div>
                            </>
                          )}
                        </WorktypeDescription>
                      </>
                    )}
                  </FormController>
                </div>
                <div>
                  <FormController control={control} name="poNumber">
                    {({ field }) => (
                      <WorktypeDescription>
                        {({ editable, setEditable }) => (
                          <>
                            <div className="flex items-center space-x-3 text-lg font-semibold">
                              <dt>{t('PO Number')}:</dt>
                              <Button
                                title="Edit"
                                size="mini"
                                shape="square"
                                kind="secondary"
                                type="button"
                                onClick={() => setEditable(!editable)}
                              >
                                <Edit size={12} />
                              </Button>
                            </div>
                            <div>
                              {editable ? (
                                <div className="space-y-4 py-2">
                                  <Input
                                    name={field.name}
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                      const cleanValue = e.target.value
                                        .replace(/[^a-zA-Z0-9\s\-_/#]/g, '')
                                        .slice(0, 50);
                                      field.onChange(cleanValue || null);
                                    }}
                                    placeholder={t('Enter PO Number')}
                                    onBlur={field.onBlur}
                                    overrides={{
                                      Input: {
                                        props: {
                                          className:
                                            'placeholder:text-gray-400 placeholder:text-sm text-gray-500 font-semibold',
                                          onKeyDown: (
                                            e: React.KeyboardEvent<HTMLInputElement>,
                                          ) => {
                                            if (
                                              ['+', '-', '.', '='].includes(
                                                e.key,
                                              )
                                            ) {
                                              e.preventDefault();
                                            }
                                          },
                                        },
                                      },
                                    }}
                                  />
                                  <nav className="flex justify-end space-x-2">
                                    <Button
                                      size="mini"
                                      kind="secondary"
                                      onClick={() => setEditable(false)}
                                    >
                                      {t('Confirm')}
                                    </Button>
                                  </nav>
                                </div>
                              ) : (
                                <dd className="text-base font-semibold text-gray-500">
                                  {field.value}
                                </dd>
                              )}
                            </div>
                          </>
                        )}
                      </WorktypeDescription>
                    )}
                  </FormController>
                </div>
                {isPortDeliveryEnabled && (
                  <>
                    <div className="space-y-2">
                      <FormController control={control} name="soNumber">
                        {({ field }) => (
                          <WorktypeDescription>
                            {({ editable, setEditable }) => (
                              <>
                                <div className="flex items-center space-x-3 text-lg font-semibold">
                                  <dt>{t('SO Number (Optional)')}:</dt>
                                  <Button
                                    title="Edit"
                                    size="mini"
                                    shape="square"
                                    kind="secondary"
                                    type="button"
                                    onClick={() => setEditable(!editable)}
                                  >
                                    <Edit size={12} />
                                  </Button>
                                </div>
                                <div>
                                  {editable ? (
                                    <div className="space-y-4 py-2">
                                      <Input
                                        name={field.name}
                                        value={field.value ?? ''}
                                        onChange={(e) =>
                                          field.onChange(
                                            e.target.value.slice(0, 50) || null,
                                          )
                                        }
                                        placeholder={t('Enter SO Number')}
                                        onBlur={field.onBlur}
                                        overrides={{
                                          Input: {
                                            props: {
                                              className:
                                                'placeholder:text-gray-400 placeholder:text-sm text-gray-500 font-semibold',
                                            },
                                          },
                                        }}
                                      />
                                      <nav className="flex justify-end space-x-2">
                                        <Button
                                          size="mini"
                                          kind="secondary"
                                          onClick={() => setEditable(false)}
                                        >
                                          {t('Confirm')}
                                        </Button>
                                      </nav>
                                    </div>
                                  ) : (
                                    <dd className="text-base font-semibold text-gray-500">
                                      {field.value}
                                    </dd>
                                  )}
                                </div>
                              </>
                            )}
                          </WorktypeDescription>
                        )}
                      </FormController>
                    </div>
                    <div className="space-y-2">
                      <FormController control={control} name="shipName">
                        {({ field }) => (
                          <WorktypeDescription>
                            {({ editable, setEditable }) => (
                              <>
                                <div className="flex items-center space-x-3 text-lg font-semibold">
                                  <dt>{t('Ship Name (Optional)')}:</dt>
                                  <Button
                                    title="Edit"
                                    size="mini"
                                    shape="square"
                                    kind="secondary"
                                    type="button"
                                    onClick={() => setEditable(!editable)}
                                  >
                                    <Edit size={12} />
                                  </Button>
                                </div>
                                <div>
                                  {editable ? (
                                    <div className="space-y-4 py-2">
                                      <Input
                                        name={field.name}
                                        value={field.value ?? ''}
                                        onChange={(e) =>
                                          field.onChange(
                                            e.target.value.slice(0, 50) || null,
                                          )
                                        }
                                        placeholder={t('Enter Ship Name')}
                                        onBlur={field.onBlur}
                                        overrides={{
                                          Input: {
                                            props: {
                                              className:
                                                'placeholder:text-gray-400 placeholder:text-sm text-gray-500 font-semibold',
                                            },
                                          },
                                        }}
                                      />
                                      <nav className="flex justify-end space-x-2">
                                        <Button
                                          size="mini"
                                          kind="secondary"
                                          onClick={() => setEditable(false)}
                                        >
                                          {t('Confirm')}
                                        </Button>
                                      </nav>
                                    </div>
                                  ) : (
                                    <dd className="text-base font-semibold text-gray-500">
                                      {field.value}
                                    </dd>
                                  )}
                                </div>
                              </>
                            )}
                          </WorktypeDescription>
                        )}
                      </FormController>
                    </div>
                  </>
                )}
              </div>
              <div className="space-y-2">
                <FormController control={control} name="serviceLocation">
                  {({ field }) => (
                    <>
                      <div>
                        <dt className="flex items-center space-x-3 text-lg font-semibold">
                          <span>{t('Delivery Location')}</span>
                          <ServiceLocationField
                            {...field}
                            setValue={setValue}
                            isPickupLocationEnabled={isPickupLocationEnabled}
                          />
                        </dt>
                        <dl className="space-y-1 pt-3">
                          <div className="flex items-start space-x-4">
                            <dt
                              aria-label="Address"
                              className="pt-1.5 text-base text-gray-500"
                            >
                              <Navigation size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {isSameAsBilling(field.value as Address) ? (
                                <span className="text-base font-semibold text-gray-500">
                                  ({t('same as billing')})
                                </span>
                              ) : (
                                formatAddress(field.value as Address)
                              )}
                            </dd>
                          </div>
                          <div className="flex space-x-4 ">
                            <dt
                              aria-label="Phone number"
                              className="pt-1.5 text-base text-gray-500"
                            >
                              <Phone size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {invoice.workRequestDetails.phone}
                            </dd>
                          </div>
                          <div className="flex space-x-4 ">
                            <dt
                              aria-label="email address"
                              className="pt-1.5 text-base text-gray-500"
                            >
                              <Mail size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {invoice.workRequestDetails.email}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </>
                  )}
                </FormController>
              </div>
              <div className="space-y-2">
                <FormController control={control} name="pickupLocation">
                  {({ field }) => (
                    <>
                      <div>
                        <dt className="flex items-center space-x-3 text-lg font-semibold">
                          <span>{t('Pickup Location')}</span>
                          <PickupLocationField {...field} setValue={setValue} />
                        </dt>
                        <dl className="space-y-1 pt-3">
                          <div className="flex items-start space-x-4">
                            <dt
                              aria-label="Address"
                              className="pt-1.5 text-base text-gray-500"
                            >
                              <Navigation size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {isSameAsBilling(field.value as Address) ? (
                                <span className="text-base font-semibold text-gray-500">
                                  ({t('same as billing')})
                                </span>
                              ) : (
                                formatAddress(field.value as Address)
                              )}
                            </dd>
                          </div>
                          <div className="flex space-x-4 ">
                            <dt
                              aria-label="Phone number"
                              className="pt-1.5 text-base text-gray-500"
                            >
                              <Phone size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {pickupLocationData?.phone ||
                                invoice.workRequestDetails.phone}
                            </dd>
                          </div>
                          <div className="flex space-x-4 ">
                            <dt
                              aria-label="email address"
                              className="pt-1.5 text-base text-gray-500"
                            >
                              <Mail size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {invoice.workRequestDetails.email}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </>
                  )}
                </FormController>
              </div>
            </dl>
          ) : (
            <dl className="grid gap-x-6 gap-y-4 font-prompt lg:grid-cols-2 xl:grid-cols-3">
              <div className="col-span-full flex items-center space-x-2">
                <div>
                  <dt className="sr-only">Work Request number</dt>
                  <dd className="text-4xl font-semibold text-brand-primary">
                    #{invoice.workRequestDetails.num} -{' '}
                  </dd>
                </div>
                <div>
                  <dt className="sr-only">status</dt>
                  <dd>
                    <StatusBadge
                      size="large"
                      status={invoice.workRequestDetails.progress}
                    />
                  </dd>
                </div>
              </div>
              <div className="space-y-2">
                <FormController control={control} name="billTo">
                  {({ field }) => (
                    <>
                      <div>
                        <dt className="flex items-center space-x-3 text-lg font-semibold">
                          <span>{t('Bill To')}: </span>
                          <BillToField {...field} setValue={setValue} />
                        </dt>
                        <dl className="space-y-3">
                          <div className="flex items-center">
                            <dt className="sr-only">{t('Customer Name')}</dt>
                            <dd className="pt-2 text-base font-semibold text-gray-500">
                              {`${field.value.firstName} ${field.value.lastName}`}
                            </dd>
                          </div>
                          <div className="flex items-start space-x-4">
                            <dt
                              aria-label="Address"
                              className="pt-1.5 text-base text-gray-500"
                            >
                              <Navigation size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {formatAddress(field.value.address)}
                            </dd>
                          </div>
                          <div className="flex space-x-4">
                            <dt
                              aria-label="Phone number"
                              className="pt-1 text-base text-gray-500"
                            >
                              <Phone size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {field.value.phone}
                            </dd>
                          </div>
                          <div className="flex space-x-4">
                            <dt
                              aria-label="email address"
                              className="pt-1.5 text-base text-gray-500"
                            >
                              <Mail size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {field.value.email}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </>
                  )}
                </FormController>
              </div>
              <div className="space-y-2">
                <FormController control={control} name="serviceLocation">
                  {({ field }) => (
                    <>
                      <div>
                        <dt className="flex items-center space-x-3 text-lg font-semibold">
                          <span>{t('Service Address')}</span>
                          <ServiceLocationField
                            {...field}
                            setValue={setValue}
                            isPickupLocationEnabled={isPickupLocationEnabled}
                          />
                        </dt>
                        <dl className="space-y-1 pt-3">
                          <div className="flex items-start space-x-4">
                            <dt
                              aria-label="Address"
                              className="pt-1.5 text-base text-gray-500"
                            >
                              <Navigation size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {isSameAsBilling(field.value as Address) ? (
                                <span className="text-base font-semibold text-gray-500">
                                  ({t('same as billing')})
                                </span>
                              ) : (
                                formatAddress(field.value as Address)
                              )}
                            </dd>
                          </div>
                          <div className="flex space-x-4 ">
                            <dt
                              aria-label="Phone number"
                              className="pt-1.5 text-base text-gray-500"
                            >
                              <Phone size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {invoice.workRequestDetails.phone}
                            </dd>
                          </div>
                          <div className="flex space-x-4 ">
                            <dt
                              aria-label="email address"
                              className="pt-1.5 text-base text-gray-500"
                            >
                              <Mail size={16} />
                            </dt>
                            <dd className="text-base text-gray-500">
                              {invoice.workRequestDetails.email}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </>
                  )}
                </FormController>
              </div>
              <div className="flex flex-col space-y-4">
                <div className="col-span-full space-y-2 xl:col-span-1">
                  <FormController
                    control={control}
                    name="description"
                  >
                    {({ field }) => (
                      <>
                        <WorktypeDescription>
                          {({ editable, setEditable }) => (
                            <>
                              <div className="flex items-center space-x-3 text-lg font-semibold">
                                <dt>{t('Work Details')}</dt>

                                <Button
                                  title="Edit"
                                  size="mini"
                                  shape="square"
                                  kind="secondary"
                                  type="button"
                                  onClick={() => setEditable(!editable)}
                                >
                                  <Edit size={12} />
                                </Button>
                              </div>
                              <div>
                                {editable ? (
                                  <div className="space-y-4 py-2">
                                    <Textarea
                                      name={field.name}
                                      value={field.value}
                                      onChange={(e) => {
                                        field.onChange(e.target.value);
                                      }}
                                      placeholder={t('Description (Optional)')}
                                      onBlur={field.onBlur}
                                    />
                                    <nav className="flex justify-end space-x-2">
                                      <Button
                                        size="mini"
                                        kind="secondary"
                                        onClick={() => setEditable(false)}
                                      >
                                        {t('Confirm')}
                                      </Button>
                                    </nav>
                                  </div>
                                ) : (
                                  <dd className="text-base text-gray-500 line-clamp-5">
                                    {field.value ?? 'No description'}
                                  </dd>
                                )}
                              </div>
                            </>
                          )}
                        </WorktypeDescription>
                      </>
                    )}
                  </FormController>
                </div>
                <div>
                  <FormController control={control} name="poNumber">
                    {({ field }) => (
                      <WorktypeDescription>
                        {({ editable, setEditable }) => (
                          <>
                            <div className="flex items-center space-x-3 text-lg font-semibold">
                              <dt>{t('PO Number')}:</dt>
                              <Button
                                title="Edit"
                                size="mini"
                                shape="square"
                                kind="secondary"
                                type="button"
                                onClick={() => setEditable(!editable)}
                              >
                                <Edit size={12} />
                              </Button>
                            </div>
                            <div>
                              {editable ? (
                                <div className="space-y-4 py-2">
                                  <Input
                                    name={field.name}
                                    value={field.value ?? ''}
                                    onChange={(e) => {
                                      const cleanValue = e.target.value
                                        .replace(/[^a-zA-Z0-9\s\-_/#]/g, '')
                                        .slice(0, 50);
                                      field.onChange(cleanValue || null);
                                    }}
                                    placeholder={t('Enter PO Number')}
                                    onBlur={field.onBlur}
                                    overrides={{
                                      Input: {
                                        props: {
                                          className:
                                            'placeholder:text-gray-400 placeholder:text-sm text-gray-500 font-semibold',
                                          onKeyDown: (
                                            e: React.KeyboardEvent<HTMLInputElement>,
                                          ) => {
                                            if (
                                              ['+', '-', '.', '='].includes(
                                                e.key,
                                              )
                                            ) {
                                              e.preventDefault();
                                            }
                                          },
                                        },
                                      },
                                    }}
                                  />
                                  <nav className="flex justify-end space-x-2">
                                    <Button
                                      size="mini"
                                      kind="secondary"
                                      onClick={() => setEditable(false)}
                                    >
                                      {t('Confirm')}
                                    </Button>
                                  </nav>
                                </div>
                              ) : (
                                <dd className="text-base font-semibold text-gray-500">
                                  {field.value}
                                </dd>
                              )}
                            </div>
                          </>
                        )}
                      </WorktypeDescription>
                    )}
                  </FormController>
                </div>
                {isPortDeliveryEnabled && (
                  <>
                    <div className="space-y-2">
                      <FormController control={control} name="soNumber">
                        {({ field }) => (
                          <WorktypeDescription>
                            {({ editable, setEditable }) => (
                              <>
                                <div className="flex items-center space-x-3 text-lg font-semibold">
                                  <dt>{t('SO Number (Optional)')}:</dt>
                                  <Button
                                    title="Edit"
                                    size="mini"
                                    shape="square"
                                    kind="secondary"
                                    type="button"
                                    onClick={() => setEditable(!editable)}
                                  >
                                    <Edit size={12} />
                                  </Button>
                                </div>
                                <div>
                                  {editable ? (
                                    <div className="space-y-4 py-2">
                                      <Input
                                        name={field.name}
                                        value={field.value ?? ''}
                                        onChange={(e) =>
                                          field.onChange(
                                            e.target.value.slice(0, 50) || null,
                                          )
                                        }
                                        placeholder={t('Enter SO Number')}
                                        onBlur={field.onBlur}
                                        overrides={{
                                          Input: {
                                            props: {
                                              className:
                                                'placeholder:text-gray-400 placeholder:text-sm text-gray-500 font-semibold',
                                            },
                                          },
                                        }}
                                      />
                                      <nav className="flex justify-end space-x-2">
                                        <Button
                                          size="mini"
                                          kind="secondary"
                                          onClick={() => setEditable(false)}
                                        >
                                          {t('Confirm')}
                                        </Button>
                                      </nav>
                                    </div>
                                  ) : (
                                    <dd className="text-base font-semibold text-gray-500">
                                      {field.value}
                                    </dd>
                                  )}
                                </div>
                              </>
                            )}
                          </WorktypeDescription>
                        )}
                      </FormController>
                    </div>
                    <div className="space-y-2">
                      <FormController control={control} name="shipName">
                        {({ field }) => (
                          <WorktypeDescription>
                            {({ editable, setEditable }) => (
                              <>
                                <div className="flex items-center space-x-3 text-lg font-semibold">
                                  <dt>{t('Ship Name (Optional)')}:</dt>
                                  <Button
                                    title="Edit"
                                    size="mini"
                                    shape="square"
                                    kind="secondary"
                                    type="button"
                                    onClick={() => setEditable(!editable)}
                                  >
                                    <Edit size={12} />
                                  </Button>
                                </div>
                                <div>
                                  {editable ? (
                                    <div className="space-y-4 py-2">
                                      <Input
                                        name={field.name}
                                        value={field.value ?? ''}
                                        onChange={(e) =>
                                          field.onChange(
                                            e.target.value.slice(0, 50) || null,
                                          )
                                        }
                                        placeholder={t('Enter Ship Name')}
                                        onBlur={field.onBlur}
                                        overrides={{
                                          Input: {
                                            props: {
                                              className:
                                                'placeholder:text-gray-400 placeholder:text-sm text-gray-500 font-semibold',
                                            },
                                          },
                                        }}
                                      />
                                      <nav className="flex justify-end space-x-2">
                                        <Button
                                          size="mini"
                                          kind="secondary"
                                          onClick={() => setEditable(false)}
                                        >
                                          {t('Confirm')}
                                        </Button>
                                      </nav>
                                    </div>
                                  ) : (
                                    <dd className="text-base font-semibold text-gray-500">
                                      {field.value}
                                    </dd>
                                  )}
                                </div>
                              </>
                            )}
                          </WorktypeDescription>
                        )}
                      </FormController>
                    </div>
                  </>
                )}
              </div>
            </dl>
          )}
          {showPreview && <PreviewPanel />}
          <div
            className={clsx('gap-x-6 gap-y-4 font-prompt xl:grid-cols-2', {
              hidden: showPreview,
              grid: !showPreview,
            })}
          >
            <div className="col-span-full overflow-hidden border-b pb-4">
              <div className="font-prompt text-lg font-semibold text-black">
                {t('Invoice Items')}
              </div>
              <LineItems
                control={control}
                errors={lineItemErrors}
                items={lineItemsField.fields}
                workRequestId={invoice.workRequestDetails.id}
                onAdd={lineItemsField.append}
                onRemove={lineItemsField.remove}
                onUpdate={lineItemsField.update}
              />
            </div>
            <div className="grid gap-x-5 md:gap-y-5 lg:grid-cols-2 lg:gap-y-0">
              <div className="grid gap-y-1.5 font-prompt">
                <div className="flex space-x-3">
                  <div className="w-24 shrink-0 text-base text-black">
                    {t('Sub Total')}
                  </div>
                  <div className="flex-1 text-base text-gray-500">
                    {priceFormatter().format(computations.subTotal)}
                  </div>
                </div>
                <div className="flex space-x-3">
                  <dt className="w-24 shrink-0 text-base text-black">
                    {t('Taxable')}
                  </dt>
                  <div className="flex-1 text-base text-gray-500">
                    {priceFormatter().format(computations.taxableAmount)}
                  </div>
                </div>
                <div className="flex space-x-3">
                  <dt className="w-24 shrink-0 text-base text-black">
                    {t('Tax %')}
                  </dt>
                  <div className="flex-1 text-base text-gray-500">
                    <FormController
                      control={control}
                      name="computations.taxPercentage"
                      overrides={{
                        ControlContainer: { props: { className: 'm-0' } },
                      }}
                    >
                      {() => (
                        <input
                          {...register('computations.taxPercentage', {
                            valueAsNumber: true,
                          })}
                          type="text"
                          className="w-full rounded border border-brand-primary-100 px-2"
                          disabled={computations.taxableAmount === 0}
                          autoComplete="off"
                        />
                      )}
                    </FormController>
                  </div>
                </div>
                <div className="flex space-x-3">
                  <div className="w-24 shrink-0 text-base text-black">
                    {t('Tax')}
                  </div>
                  <div className="flex-1 text-base text-gray-500">
                    {priceFormatter().format(computations.taxedAmount)}
                  </div>
                </div>
                <div className="flex space-x-3">
                  <div className="w-24 shrink-0 text-base text-black">
                    {t('Deposit')}
                  </div>
                  <div className="flex space-x-2 text-base text-gray-500">
                    <span>
                      {priceFormatter().format(
                        (computations.depositPercentage / 100) *
                          (computations.subTotal + computations.taxedAmount),
                      )}
                    </span>
                    <div>
                      <span>{`(${computations.depositPercentage}%)`}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex flex-col space-y-2">
                  <div className="flex justify-between">
                    <div className="text-base font-semibold text-black">
                      {t('Total')}
                    </div>
                    <div className="text-base text-brand-primary">
                      {priceFormatter().format(computations.total)}
                    </div>
                  </div>

                  {paymentHistory && paymentHistory.length > 0 && (
                    <div className="flex justify-between">
                      <div className="text-base font-semibold text-black">
                        {t('Total Payments')}
                      </div>
                      <div className="text-base text-green-600">
                        {priceFormatter().format(
                          calculateTotalPayments(paymentHistory),
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between border-t pt-2">
                    <div className="text-base font-semibold text-black">
                      {t('Remaining Balance')}
                    </div>
                    <div
                      className={clsx(
                        'text-base',
                        remainingBalance < 0
                          ? 'text-red-600'
                          : 'text-brand-primary',
                      )}
                    >
                      {priceFormatter().format(Math.max(0, remainingBalance))}
                    </div>
                  </div>

                  {remainingBalance < 0 && (
                    <div className="mt-2 flex items-center gap-2 text-red-500">
                      <AlertIcon className="h-4 w-4" />
                      <span className="text-sm">
                        {t('overpayment_warning', {
                          amount: priceFormatter().format(
                            Math.abs(remainingBalance),
                          ),
                        })}
                      </span>
                    </div>
                  )}

                  {isQuickbooksConnected && invoice?.breakdown?.quickbooksId && (
                    <div className="flex justify-between">
                      <div className="text-base font-semibold text-black">
                        {t('Quickbooks Balance')}
                      </div>
                      <div className="text-base text-brand-primary">
                        <span>
                          {priceFormatter().format(
                            invoice.breakdown?.quickbooksBalance ?? 0,
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex space-x-4 space-y-2">
                  {invoice?.breakdown?.id && isHelcimConnected && (
                    <div className="mt-4 w-full">
                      <h3 className="mb-2 text-left text-base font-semibold">
                        {t('Payment History')}
                      </h3>
                      {paymentHistory && paymentHistory.length > 0 ? (
                        <div className="overflow-hidden rounded border border-gray-200">
                          <div className="max-h-[120px] overflow-y-auto">
                            <table className="w-full border-collapse text-center">
                              <thead className="sticky top-0 z-10">
                                <tr className="bg-gray-100">
                                  <th className="border border-gray-200 px-4 py-2 text-base font-normal">
                                    {t('Date')}
                                  </th>
                                  <th className="border border-gray-200 px-4 py-2 text-base font-normal">
                                    {t('Amount')}
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {paymentHistory.map((payment) => (
                                  <tr
                                    key={payment.id}
                                    className="odd:bg-white even:bg-gray-50"
                                  >
                                    <td className="border border-gray-200 px-4 py-2 text-base font-extralight text-gray-500">
                                      {formatDate(
                                        new Date(payment.dateModified),
                                      )}
                                    </td>
                                    <td className="border border-gray-200 px-4 py-2 text-base text-brand-primary">
                                      {formatCurrencyAmount(
                                        payment.amount,
                                        payment.currency as 'USD' | 'CAD',
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <p className="w-full text-center text-gray-500">
                          {t('No payment history available')}
                        </p>
                      )}

                      {invoice.breakdown &&
                        isQuickbooksConnected &&
                        (!invoice.breakdown.quickbooksId ||
                          (paymentHistory &&
                            paymentHistory.some(
                              (payment) => !payment.quickbooksId,
                            ))) && (
                          <div className="mt-4 flex justify-end">
                            <Button
                              size="compact"
                              onClick={() => {
                                if (invoice.breakdown?.id) {
                                  syncQuickbooks(invoice.breakdown.id);
                                }
                              }}
                              isLoading={isSyncing}
                              className="flex items-center space-x-2 rounded border border-green-500 bg-white px-3 py-2 text-sm text-green-500 outline outline-green-500 transition-all duration-200 hover:bg-green-600 hover:text-white hover:outline-white"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 66 60"
                                xmlns="http://www.w3.org/2000/svg"
                                className="text-green-500 transition-colors duration-200 hover:text-white"
                                fill="currentColor"
                                aria-label="QuickBooks Icon"
                              >
                                <path d="M.5 30c0 9.665 7.835 17.5 17.5 17.5h2.5V41H18c-6.075 0-11-4.925-11-11s4.925-11 11-11h6v34c0 1.724.685 3.377 1.904 4.596 1.22 1.22 2.872 1.904 4.596 1.904v-47H18C8.335 12.5.5 20.335.5 30zM48 12.5h-2.5V19H48c6.075 0 11 4.925 11 11s-4.925 11-11 11h-6V7C42 3.41 39.09.5 35.5.5v47H48c6.252 0 12.03-3.335 15.155-8.75 3.127-5.415 3.127-12.085 0-17.5C60.03 15.835 54.252 12.5 48 12.5z" />
                              </svg>
                              <span>{t('Sync QuickBooks')}</span>
                            </Button>
                          </div>
                        )}
                    </div>
                  )}
                </div>
                {/* <div className="flex space-x-3">
                    <div className="text-base text-black font-semibold w-24">
                      Due Date
                    </div>
                    <div className="text-gray-500 font-semibold text-base">
                      {format(new Date(), 'MM/dd/yyyy')}
                    </div>
                  </div> */}
              </div>
            </div>
            {centralizedNotesEnabled ? (
              <section className="col-span-full xl:col-span-1">
                <Heading className="font-prompt text-lg font-semibold text-black">
                  {t('Job Notes')}
                </Heading>
                <div className="mt-4 pb-4 xl:border-b">
                  <ServiceLocationNotes
                    locationNote={{
                      type: 'JOB',
                      workRequestId: invoice.workRequestDetails.id,
                      serviceLocationId: invoice.servLoc.id,
                    }}
                    readOnly
                  />
                </div>
              </section>
            ) : (
              <section className="col-span-full xl:col-span-1">
                <Heading className="font-prompt text-lg font-semibold text-black">
                  {t('Job Notes')}
                </Heading>
                <div className="mt-4 pb-4 xl:border-b">
                  <Textarea
                    value={invoice.workRequestDetails.notes ?? ''}
                    rows={5}
                    readOnly
                  />
                </div>
              </section>
            )}
            <section className="col-span-full xl:col-span-1">
              <Heading className="font-prompt text-lg font-semibold text-black">
                {t('Invoice Notes')}
              </Heading>
              <div className="mt-4 border-b pb-4 xl:border-b-0">
                <ServiceLocationNotes
                  locationNote={{
                    type: 'INVOICE',
                    workRequestId: invoice.workRequestDetails.id,
                    serviceLocationId: invoice.servLoc.id,
                  }}
                />
              </div>
            </section>
            <WorkRequestAttachments
              workRequestId={invoice.workRequestDetails.id}
            />
            <WorkRequestChecklists
              workRequestId={invoice.workRequestDetails.id}
            />
            <Signature />
          </div>
        </form>
      </CardContent>
      {isHelcimConnected && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          amount={invoice.breakdown?.total ?? 0}
          remainingBalance={Math.max(0, remainingBalance)}
          invoiceId={invoice.breakdown?.id ?? 0}
          currency={currencyPreference}
          onPaymentComplete={handlePaymentComplete}
        />
      )}
    </Card>
  );
}

const numberSchema = z.coerce.number().catch(0);

function Signature() {
  const { t } = useLocale();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const workRequestId = numberSchema.parse(
    searchParams.get('workRequestId') ?? params.workRequestId,
  );

  const query = useImagesQuery(
    {
      params: { workRequestId },
      purpose: 'Signature',
    },
    { skip: workRequestId === 0 },
  );

  return (
    <fieldset className="space-y-2">
      <legend className="font-prompt text-lg font-semibold">
        {t('Signature')}
      </legend>
      <QueryResolver query={query}>
        {(signatureQuery) => (
          <>
            {signatureQuery.length === 0 ? (
              <div className="text-base font-semibold text-gray-300">
                {t('signature_empty')}
              </div>
            ) : (
              <ul className="flex space-x-4 overflow-x-auto">
                {signatureQuery.map((image) => (
                  <li
                    key={image.id}
                    className="h-40 w-40 shrink-0 overflow-hidden rounded"
                  >
                    <DataStoreImage
                      imageId={Number(image.id)}
                      className="h-full w-full object-cover"
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </QueryResolver>
    </fieldset>
  );
}

export function BasicDetails({ workRequestId }: { workRequestId: number }) {
  const query = useInvoiceQuery(workRequestId, { skip: workRequestId === 0 });
  const { t } = useLocale();
  return (
    <Card className="h-full overflow-y-auto">
      <QueryResolver query={query}>
        {(job) => (
          <>
            <CardHeader>
              <CardTitle>{t('Job Details')}</CardTitle>
            </CardHeader>
            <CardContent>
              <WorkRequestOverview workRequest={job.workRequestDetails} />
            </CardContent>
          </>
        )}
      </QueryResolver>
    </Card>
  );
}

function ServiceLocationField({
  value,
  setValue,
  isPickupLocationEnabled,
}: {
  setValue: UseFormSetValue<FormFields>;
  isPickupLocationEnabled: boolean;
} & ControllerRenderProps<FormFields, 'serviceLocation'>) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLocale();

  return (
    <>
      <Button
        title="Edit"
        size="mini"
        shape="square"
        kind="secondary"
        type="button"
        onClick={() => setIsOpen(true)}
      >
        <Edit size={12} />
      </Button>
      <Modal onClose={() => setIsOpen(false)} closeable isOpen={isOpen}>
        <ModalHeader>
          {isPickupLocationEnabled
            ? t('Change delivery location')
            : t('Change service location')}
        </ModalHeader>
        <ModalBody>
          <ServLocAddressForm
            id="invoice-edit-service-location-address-form"
            initialValues={{ ...value }}
            onSubmit={(data) => {
              setValue('serviceLocation', {
                ...data,
                id: value.id,
                serviceLocationId: value.serviceLocationId,
              });
              setIsOpen(false);
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            size="compact"
            kind="tertiary"
            onClick={() => setIsOpen(false)}
          >
            {t('Close')}
          </Button>
          <Button
            size="compact"
            form="invoice-edit-service-location-address-form"
            type="submit"
            className="ml-4"
          >
            {t('Confirm')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

function PickupLocationField({
  value,
  setValue,
}: {
  setValue: UseFormSetValue<FormFields>;
} & ControllerRenderProps<FormFields, 'pickupLocation'>) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLocale();

  return (
    <>
      <Button
        title="Edit"
        size="mini"
        shape="square"
        kind="secondary"
        type="button"
        onClick={() => setIsOpen(true)}
      >
        <Edit size={12} />
      </Button>
      <Modal onClose={() => setIsOpen(false)} closeable isOpen={isOpen}>
        <ModalHeader>{t('Change pickup location')}</ModalHeader>
        <ModalBody>
          <ServLocAddressForm
            id="invoice-edit-pickup-location-address-form"
            initialValues={{ ...value }}
            onSubmit={(data) => {
              setValue('pickupLocation', {
                ...data,
                id: value?.id || 0,
                serviceLocationId: value?.serviceLocationId || 0,
              });
              setIsOpen(false);
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            size="compact"
            kind="tertiary"
            onClick={() => setIsOpen(false)}
          >
            {t('Close')}
          </Button>
          <Button
            size="compact"
            form="invoice-edit-pickup-location-address-form"
            type="submit"
            className="ml-4"
          >
            {t('Confirm')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

function BillToField({
  value,
  setValue,
}: {
  setValue: UseFormSetValue<FormFields>;
} & ControllerRenderProps<FormFields, 'billTo'>) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFormValid, setIsFormValid] = useState(true);
  const { t } = useLocale();

  return (
    <>
      <Button
        title="Edit"
        size="mini"
        shape="square"
        kind="secondary"
        type="button"
        onClick={() => setIsOpen(true)}
      >
        <Edit size={12} />
      </Button>
      <Modal
        onClose={() => setIsOpen(false)}
        closeable
        isOpen={isOpen}
        overrides={{
          Dialog: {
            style: {
              width: '80vw',
              maxWidth: '800px',
              minWidth: '400px',
            },
          },
        }}
      >
        <ModalHeader>{t('Change Bill to')}</ModalHeader>
        <ModalBody>
          <BillToForm
            id="invoice-edit-bill-to-form"
            initialValues={{
              ...value,
              businessName: value.businessName || '',
              address: {
                ...value.address,
                isVerified: value.address.isVerified || false,
              },
            }}
            onValidationChange={setIsFormValid}
            onSubmit={(data) => {
              const isNewCustomer = data.id !== value.id;

              setValue('billTo', {
                ...data,
                address: isNewCustomer
                  ? ({ ...data.address, id: 0 } as typeof value.address)
                  : ({
                      id: value.address.id,
                      ...data.address,
                    } as typeof value.address),
              });
              setIsOpen(false);
            }}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            size="compact"
            kind="tertiary"
            onClick={() => setIsOpen(false)}
          >
            {t('Close')}
          </Button>
          <Button
            size="compact"
            form="invoice-edit-bill-to-form"
            disabled={!isFormValid}
          >
            {t('Confirm')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

function WorktypeDescription({
  children,
}: {
  children: (args: {
    editable?: boolean;
    setEditable: (value: boolean) => void;
  }) => React.ReactNode;
}) {
  const [editable, setEditable] = useState(false);
  return <>{children({ editable, setEditable })}</>;
}

function PreviewPanel() {
  const { t } = useLocale();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const id = numberSchema.parse(
    searchParams.get('workRequestId') ?? params.workRequestId,
  );

  const query = usePreviewPDFQuery({
    workRequestId: id,
    templateType: 'Invoice',
  });

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">{t('Preview Invoice')}</h2>
        {/* Commenting this out for now since there is already a send button in the invoice details header */}
        {/* <Button
          size="compact"
          type="button"
          startEnhancer={<Mail className="w-4 h-4" />}
        >
          Send Invoice
        </Button> */}
      </div>
      <QueryResolver query={query}>
        {(pdfData) => (
          <div className="aspect-h-1 aspect-w-1">
            <iframe
              title="Hello"
              className="rounded-lg border bg-white"
              // src="https://example.com/test.pdf#page=2&toolbar=1"
              src={`data:application/pdf;base64,${pdfData.dataB64}`}
              width="100%"
              height="100%"
            />
          </div>
        )}
      </QueryResolver>
    </section>
  );
}
